'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageProvider';
import { CookieConsent } from './CookieConsent';

/**
 * Shared chrome for the v2 (editorial) site.
 * Design tokens, layout, atoms (Rule / Eyebrow / Stat), motion helpers.
 *
 * All color / hairline values are tokens in globals.css. The constants
 * exported here mirror those tokens for use in inline `style={{ ... }}`
 * (where Tailwind/CSS-vars-in-class isn't possible).
 */

export const ACCENT          = '#d6342c';                       // anchored crimson, less iOS-buzz
export const ACCENT_SOFT     = 'rgba(214, 52, 44, 0.09)';        // subtle accent fill
export const HAIRLINE_SOFT   = 'rgba(255, 248, 240, 0.06)';      // within-section
export const HAIRLINE        = 'rgba(255, 248, 240, 0.12)';      // default (item rows)
export const HAIRLINE_STRONG = 'rgba(255, 248, 240, 0.22)';      // section breaks
export const MUTED           = 'rgba(255, 248, 240, 0.55)';
export const SURFACE         = '#161412';                        // card / inset bg
export const SURFACE_2       = '#1f1c19';                        // elevated

export type Lang = 'CN' | 'EN' | 'DE';

const NAV_COPY = {
  CN: { about: '关于 PathIN', gravilens: 'GraviLens 引力透镜', qiaodong: 'Qiaodong 桥洞', events: '活动', contact: '联系' },
  EN: { about: 'About', gravilens: 'GraviLens 引力透镜', qiaodong: 'Qiaodong 桥洞', events: 'Events', contact: 'Contact' },
  DE: { about: 'Über uns', gravilens: 'GraviLens 引力透镜', qiaodong: 'Qiaodong 桥洞', events: 'Events', contact: 'Kontakt' },
};

const FOOTER_COPY = {
  CN: {
    title: '联系我们',
    lede: '巡演合作、艺人邀约、媒体咨询、一般问题——欢迎邮件联系，或使用联系表单。',
    formCta: '打开联系表单 →',
    labels: { email: '邮箱', address: '地址', social: '社交', status: '法律状态', donations: '捐赠' },
    statusBody: '已注册公益协会 (Eingetragener gemeinnütziger Verein)',
    statusSub: '慕尼黑 · 2025 年 4 月成立',
    donateBody: '可开具德国税务认可的公益捐赠收据。',
    donateCta: '支持 PathIN →',
    impressum: '法律声明',
    datenschutz: '隐私政策',
    terms: '使用条款',
    rights: '保留所有权利',
  },
  EN: {
    title: 'Get in touch',
    lede: 'For booking, partnerships, press or general inquiries — write to the address below or use the contact form.',
    formCta: 'Open contact form →',
    labels: { email: 'Email', address: 'Address', social: 'Social', status: 'Status', donations: 'Donations' },
    statusBody: 'Eingetragener gemeinnütziger Verein (e.V.)',
    statusSub: 'Munich · Founded April 2025',
    donateBody: 'Tax-deductible donation receipts available on request.',
    donateCta: 'Support PathIN →',
    impressum: 'Legal Notice',
    datenschutz: 'Privacy',
    terms: 'Terms',
    rights: 'All rights reserved',
  },
  DE: {
    title: 'Kontakt',
    lede: 'Für Booking, Kooperationen, Presse oder allgemeine Anfragen — schreiben Sie uns oder nutzen Sie das Kontaktformular.',
    formCta: 'Kontaktformular öffnen →',
    labels: { email: 'E-Mail', address: 'Adresse', social: 'Social Media', status: 'Status', donations: 'Spenden' },
    statusBody: 'Eingetragener gemeinnütziger Verein (e.V.)',
    statusSub: 'München · Gegründet April 2025',
    donateBody: 'Steuerlich abzugsfähige Spendenbescheinigung auf Anfrage.',
    donateCta: 'PathIN unterstützen →',
    impressum: 'Impressum',
    datenschutz: 'Datenschutz',
    terms: 'Nutzungsbedingungen',
    rights: 'Alle Rechte vorbehalten',
  },
};

export function useV2Lang(): Lang {
  const { language } = useLanguage();
  return (language ?? 'EN') as Lang;
}

/* ──────── Layout ──────── */

export function V2Layout({ children, hideFooterContact }: { children: React.ReactNode; hideFooterContact?: boolean }) {
  return (
    <div
      className="v2-canvas text-white min-h-screen antialiased v2-cjk-body"
      style={{
        fontFamily:
          '"Inter Display", Inter, "Helvetica Neue", "PingFang SC", "Hiragino Sans GB", "Noto Sans SC", "Source Han Sans CN", system-ui, -apple-system, sans-serif',
        fontFeatureSettings: '"ss01", "cv11", "palt", "kern"',
      }}
    >
      <V2Nav />
      <main>{children}</main>
      <V2Footer hideContact={hideFooterContact} />
      <CookieConsent />
    </div>
  );
}

/* ──────── Nav ──────── */

export function V2Nav() {
  const { language, setLanguage } = useLanguage();
  const lang = (language ?? 'EN') as Lang;
  const c = NAV_COPY[lang];
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0c0b0a]/85 backdrop-blur-md' : 'bg-transparent'
      }`}
      style={{ borderBottom: scrolled ? `1px solid ${HAIRLINE}` : '1px solid transparent' }}
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-6 md:px-10">
        <Link
          to="/"
          className="text-[16px] font-bold tracking-[-0.01em] text-white hover:text-white/80 transition-colors"
        >
          PathIN
        </Link>
        <div className="hidden lg:flex items-center gap-x-6 gap-y-2 text-[13px] tracking-[0.01em] text-white/80 font-medium whitespace-nowrap">
          <Link to="/about" className="hover:text-white transition-colors">{c.about}</Link>
          <Link to="/GraviLens" className="hover:text-white transition-colors">{c.gravilens}</Link>
          <Link to="/Qiaodong" className="hover:text-white transition-colors">{c.qiaodong}</Link>
          <Link to="/events" className="hover:text-white transition-colors">{c.events}</Link>
          <Link to="/kontakt" className="hover:text-white transition-colors">{c.contact}</Link>
        </div>
        <div className="flex items-center gap-3 text-[12px] font-semibold">
          {(['CN', 'EN', 'DE'] as Lang[]).map((L, i) => (
            <span key={L} className="flex items-center gap-3">
              {i > 0 && <span className="text-white/30 font-normal">·</span>}
              <button
                onClick={() => setLanguage(L)}
                className={`transition-colors ${lang === L ? '' : 'text-white/65 hover:text-white'}`}
                style={lang === L ? { color: ACCENT } : undefined}
              >
                {L === 'CN' ? '中' : L}
              </button>
            </span>
          ))}
        </div>
      </div>
    </nav>
  );
}

/* ──────── Footer ──────── */

export function V2Footer({ hideContact }: { hideContact?: boolean }) {
  const { language } = useLanguage();
  const lang = (language ?? 'EN') as Lang;
  const f = FOOTER_COPY[lang];

  return (
    <footer id="contact" className="px-6 md:px-10 pt-24 pb-12">
      <div className="mx-auto max-w-[1400px]">
        {!hideContact && (
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <h4 className="v2-cjk-tight text-[clamp(36px,4.5vw,68px)] leading-[1.1] tracking-[-0.035em] font-black">
                {f.title}<span style={{ color: ACCENT }}>.</span>
              </h4>
              <p className="mt-6 text-[15px] leading-[1.7] text-white/80 max-w-[42ch]">{f.lede}</p>
              <Link
                to="/kontakt"
                className="inline-block mt-8 px-6 py-3 border text-[14px] tracking-wide hover:bg-white hover:text-black transition-colors"
                style={{ borderColor: 'rgba(255,255,255,0.4)' }}
              >
                {f.formCta}
              </Link>
            </div>
            <div className="md:col-span-3 md:col-start-7 text-[14px] leading-[1.65] text-white/80">
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/55 mb-3">{f.labels.email}</p>
              <p><a href="mailto:pathin@pathin.eu" className="hover:text-white">pathin@pathin.eu</a></p>
              <p><a href="mailto:gravilens@pathin.eu" className="hover:text-white">gravilens@pathin.eu</a></p>
              <p><a href="mailto:qiaodong@pathin.eu" className="hover:text-white">qiaodong@pathin.eu</a></p>

              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/55 mt-8 mb-3">{f.labels.address}</p>
              <p>PathIN e.V.<br/>Arabellastraße 5, Apt. 416<br/>81925 München, Deutschland</p>

              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/55 mt-8 mb-3">{f.labels.social}</p>
              <div className="grid grid-cols-[72px_1fr] gap-x-3 gap-y-1.5">
                <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-white/55 mt-1">Instagram</span>
                <span className="flex flex-col gap-1">
                  <a href="https://instagram.com/pathin.official" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    @pathin.official <span className="text-white/40">↗</span>
                  </a>
                  <a href="https://instagram.com/gravilens.music" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    @gravilens.music <span className="text-white/40">↗</span>
                  </a>
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-white/55 mt-1">小红书</span>
                <a href="https://xhslink.com/m/2TVoKkmKWjn" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  GraviLens <span className="text-white/40">↗</span>
                </a>
              </div>
            </div>
            <div className="md:col-span-3 md:col-start-10 text-[14px] leading-[1.65] text-white/80">
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/55 mb-3">{f.labels.status}</p>
              <p>{f.statusBody}</p>
              <p className="text-white/55 mt-1 text-[12px]">{f.statusSub}</p>

              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/55 mt-8 mb-3">{f.labels.donations}</p>
              <p>{f.donateBody}</p>
              <Link to="/donate" className="inline-block mt-3 underline underline-offset-4 text-white hover:no-underline">
                {f.donateCta}
              </Link>
            </div>
          </div>
        )}

        <div
          className={`${hideContact ? '' : 'mt-24'} pt-8 flex flex-col md:flex-row items-baseline justify-between gap-4 text-[12px] text-white/55 font-mono uppercase tracking-wider border-t`}
          style={{ borderColor: HAIRLINE }}
        >
          <span>© 2026 PathIN e.V. · {f.rights}</span>
          <div className="flex gap-6">
            <Link to="/impressum" className="hover:text-white">{f.impressum}</Link>
            <Link to="/datenschutz" className="hover:text-white">{f.datenschutz}</Link>
            <Link to="/legal/terms" className="hover:text-white">{f.terms}</Link>
          </div>
        </div>

        <div className="mt-10 select-none">
          <p className="text-[clamp(80px,18vw,260px)] leading-[0.85] font-black tracking-[-0.045em] text-white/[0.04]" aria-hidden>
            PathIN.eu
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ──────── Atoms ──────── */

export function Rule() {
  return <div className="h-px w-full" style={{ backgroundColor: HAIRLINE }} />;
}

export function Eyebrow({ children, accent }: { children: React.ReactNode; accent?: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-3 font-mono text-[clamp(11px,0.78vw,13px)] tracking-[0.18em] uppercase font-semibold"
      style={{ color: accent ? ACCENT : MUTED }}
    >
      <span
        className="inline-block h-[1px] w-8"
        style={{ backgroundColor: accent ? ACCENT : MUTED }}
        aria-hidden
      />
      {children}
    </span>
  );
}

export function Stat({ label, value, animate }: { label: string; value: string; animate?: boolean }) {
  // If value parses as a number with optional + suffix, animate via CountUp.
  // Free-form values (e.g., "2025 年 4 月") render as-is.
  const isNumeric = animate ?? /^[\d.]+\D*$/.test(value);
  return (
    <div className="flex items-baseline justify-between border-t pt-3" style={{ borderColor: HAIRLINE }}>
      <span className="font-mono text-[clamp(11px,0.78vw,12px)] tracking-[0.16em] uppercase text-white/65 font-medium">{label}</span>
      <span className="text-[clamp(20px,1.6vw,26px)] tabular-nums tracking-[-0.02em] text-white font-semibold">
        {isNumeric ? <CountUp to={value} /> : value}
      </span>
    </div>
  );
}

export function PageHeader({ kicker, title, lede }: { kicker: string; title: React.ReactNode; lede?: React.ReactNode }) {
  return (
    <RevealSection className="px-6 md:px-10 pt-32 pb-16">
      <div className="mx-auto max-w-[1400px]">
        <Eyebrow accent>{kicker}</Eyebrow>
        <h1 className="v2-cjk-tight mt-6 text-[clamp(48px,8vw,128px)] leading-[0.95] tracking-[-0.045em] font-black max-w-[20ch]">
          {title}
        </h1>
        {lede && <p className="mt-8 text-[18px] leading-[1.65] text-white/70 max-w-[60ch]">{lede}</p>}
      </div>
    </RevealSection>
  );
}

/* ──────── Inline Latin / digit helper ────────
 * Wrap Latin or numeric tokens that appear inside CJK copy so
 * the global [lang="en"] rule adds breathing room. Use sparingly —
 * mostly for run-in years, brand names, percentages, etc. */
export function L({ children, code }: { children: React.ReactNode; code?: boolean }) {
  return (
    <span lang="en" className={code ? 'font-mono tabular-nums' : 'tabular-nums'}>
      {children}
    </span>
  );
}

/* ──────── Reveal ────────
 * Scroll-triggered fade + 12px translateY on first viewport entry.
 * Uses native IntersectionObserver. Runs once; respects prefers-reduced-motion.
 * Apply at section level — not on every paragraph (that becomes noise). */
export function Reveal({
  children,
  as: Tag = 'div',
  delay = 0,
  className = '',
  ...rest
}: {
  children: React.ReactNode;
  as?: keyof React.JSX.IntrinsicElements;
  delay?: number;
  className?: string;
  [key: string]: unknown;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          window.setTimeout(() => setShown(true), delay);
          obs.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);

  // Cast for ref + className polymorphism
  const Component = Tag as unknown as React.ComponentType<Record<string, unknown>>;
  return (
    <Component
      ref={ref as React.Ref<HTMLElement>}
      className={`v2-reveal${shown ? ' is-visible' : ''} ${className}`}
      {...rest}
    >
      {children}
    </Component>
  );
}

/* ──────── RevealSection ────────
 * Drop-in replacement for <RevealSection> that fades + slides in on first viewport entry.
 * Use everywhere a plain <RevealSection> would go in V2 pages. */
export function RevealSection(
  props: { children: React.ReactNode; delay?: number } & React.HTMLAttributes<HTMLElement>,
) {
  return <Reveal as="section" {...props} />;
}

/* ──────── CountUp ────────
 * Animated integer / "N+" stat. Reads the trailing "+" if present.
 * Triggers on viewport entry (once). prefers-reduced-motion = jumps to final.
 * Used for hero stats — gives "5 / 11+" perceived weight on entry. */
export function CountUp({
  to,
  duration = 1200,
  className,
}: {
  to: string;
  duration?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [value, setValue] = useState(0);
  const [done, setDone] = useState(false);

  // Parse "11+" → number 11, suffix "+"
  const match = to.match(/^([\d.]+)(\D*)$/);
  const target = match ? parseFloat(match[1]) : 0;
  const suffix = match ? match[2] : '';

  useEffect(() => {
    const el = ref.current;
    if (!el || done) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !target || Number.isNaN(target)) {
      setValue(target);
      setDone(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        const start = performance.now();
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          // ease-out cubic-bezier(0.16, 1, 0.3, 1) approximation
          const eased = 1 - Math.pow(1 - t, 3);
          setValue(Math.round(target * eased * 10) / 10);
          if (t < 1) requestAnimationFrame(tick);
          else {
            setValue(target);
            setDone(true);
          }
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.2 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration, done]);

  // Render integer if target is integer; else preserve 1 decimal
  const display = Number.isInteger(target) ? Math.round(value) : value.toFixed(1);
  return (
    <span ref={ref} className={className}>
      {display}{suffix}
    </span>
  );
}

/* ──────── DisplayTitle ────────
 * Multiline display title.
 * Per-line trailing CJK punctuation 。？！ is converted to half-width . ? !
 * for tighter visual rhythm in big display type. The FINAL line's trailing
 * punctuation is colored ACCENT (the red-dot signature motif).
 * Splits on `\n`. Latin/EN titles flow through unchanged. */
export function DisplayTitle({ text }: { text: string }) {
  const lines = text.split('\n');
  const lastIdx = lines.length - 1;
  return (
    <>
      {lines.map((line, i) => {
        const isLast = i === lastIdx;
        const m = line.match(/[。.?!？！]+$/);
        const trailing = m?.[0] ?? '';
        const body = trailing ? line.slice(0, -trailing.length) : line;
        const punct = trailing
          .replace(/。/g, '.')
          .replace(/？/g, '?')
          .replace(/！/g, '!');
        return (
          <span key={i} className="block">
            {body}
            {punct &&
              (isLast ? (
                <span lang="en" style={{ color: ACCENT }}>{punct}</span>
              ) : (
                <span lang="en">{punct}</span>
              ))}
          </span>
        );
      })}
    </>
  );
}

export function formatDate(iso: string, lang: Lang) {
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return { month: '', day: '', year: '' };
  const monthsCN = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];
  const month =
    lang === 'CN'
      ? monthsCN[d.getMonth()]
      : d.toLocaleString(lang === 'DE' ? 'de' : 'en', { month: 'short' }).toUpperCase();
  return { month, day: String(d.getDate()).padStart(2, '0'), year: String(d.getFullYear()) };
}

export function venueShort(loc?: string) {
  if (!loc) return '';
  const parts = loc.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? '';
  if (parts.length === 2) return parts.join(' · ');
  // 3+ 段（含完整街道地址）："Venue, [街道], [邮编] City, Country" → "Venue · City"
  const venue = parts[0];
  const city = parts[parts.length - 2].replace(/^\d{4,6}\s+/, '');
  return `${venue} · ${city}`;
}
